import { useState, useEffect, useRef, useCallback } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { supabase } from '@/integrations/supabase/client';
import { Loader2, Download, X, AlertCircle, RefreshCw } from 'lucide-react';
import { toast } from 'sonner';

interface AutomationJobImageGalleryProps {
  jobId: string;
  jobName: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

interface GeneratedImage {
  id: string;
  image_url: string;
  b64_json?: string | null;
  storage_path: string | null;
  prompt: string;
  model: string;
  created_at: string;
}

// Helper to detect MIME type from base64 data
const detectMimeType = (base64: string): string => {
  if (base64.startsWith('/9j/')) return 'image/jpeg';
  if (base64.startsWith('iVBORw')) return 'image/png';
  if (base64.startsWith('R0lGOD')) return 'image/gif';
  if (base64.startsWith('UklGR')) return 'image/webp';
  return 'image/jpeg';
};

// Helper to get file extension from MIME type
const getExtensionFromMime = (mimeType: string): string => {
  if (mimeType.includes('jpeg') || mimeType.includes('jpg')) return 'jpg';
  if (mimeType.includes('png')) return 'png';
  if (mimeType.includes('gif')) return 'gif';
  if (mimeType.includes('webp')) return 'webp';
  return 'jpg';
};

// Placeholder SVG for lazy loading
const PLACEHOLDER_SVG = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMjAwIiBoZWlnaHQ9IjIwMCIgZmlsbD0iIzFmMjkzNyIvPjx0ZXh0IHg9IjUwJSIgeT0iNTAlIiBmb250LWZhbWlseT0iQXJpYWwiIGZvbnQtc2l6ZT0iMTQiIGZpbGw9IiM2YjcyODAiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIj5Mb2FkaW5nLi4uPC90ZXh0Pjwvc3ZnPg==';

export const AutomationJobImageGallery = ({
  jobId,
  jobName,
  open,
  onOpenChange,
}: AutomationJobImageGalleryProps) => {
  const [images, setImages] = useState<GeneratedImage[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [selectedImage, setSelectedImage] = useState<GeneratedImage | null>(null);
  
  // Lazy loading state
  const [loadedImages, setLoadedImages] = useState<Map<string, string>>(new Map());
  const [loadingImageIds, setLoadingImageIds] = useState<Set<string>>(new Set());
  const imageRefs = useRef<Map<string, HTMLDivElement>>(new Map());

  // Lazy load image data on demand
  const loadImageData = useCallback(async (imageId: string): Promise<string | null> => {
    if (loadedImages.has(imageId)) {
      return loadedImages.get(imageId)!;
    }
    
    if (loadingImageIds.has(imageId)) {
      return null;
    }
    
    setLoadingImageIds(prev => new Set(prev).add(imageId));
    
    try {
      const { data, error } = await supabase
        .from('generated_images')
        .select('b64_json')
        .eq('id', imageId)
        .single();
      
      if (error) throw error;
      
      if (data?.b64_json) {
        const mimeType = detectMimeType(data.b64_json);
        const dataUrl = `data:${mimeType};base64,${data.b64_json}`;
        setLoadedImages(prev => new Map(prev).set(imageId, dataUrl));
        return dataUrl;
      }
      return null;
    } catch (error) {
      console.error('Failed to load image data:', error);
      return null;
    } finally {
      setLoadingImageIds(prev => {
        const next = new Set(prev);
        next.delete(imageId);
        return next;
      });
    }
  }, [loadedImages, loadingImageIds]);

  // Get display URL - use cached data or placeholder
  const getDisplayUrl = useCallback((img: GeneratedImage): string => {
    if (loadedImages.has(img.id)) {
      return loadedImages.get(img.id)!;
    }
    if (img.b64_json) {
      const mimeType = detectMimeType(img.b64_json);
      return `data:${mimeType};base64,${img.b64_json}`;
    }
    if (img.image_url?.startsWith('data:image/')) {
      return img.image_url;
    }
    if (img.storage_path && img.storage_path.includes('/')) {
      const firstSlash = img.storage_path.indexOf('/');
      const bucket = img.storage_path.slice(0, firstSlash);
      const path = img.storage_path.slice(firstSlash + 1);
      const { data } = supabase.storage.from(bucket).getPublicUrl(path);
      return data.publicUrl || PLACEHOLDER_SVG;
    }
    if (img.image_url && img.image_url !== 'base64') {
      return img.image_url;
    }
    return PLACEHOLDER_SVG;
  }, [loadedImages]);

  // IntersectionObserver for lazy loading - with delay to ensure refs are ready
  useEffect(() => {
    if (!open) return;
    
    const timer = setTimeout(() => {
      const observer = new IntersectionObserver(
        (entries) => {
          entries.forEach(entry => {
            if (entry.isIntersecting) {
              const imageId = entry.target.getAttribute('data-image-id');
              if (imageId && !loadedImages.has(imageId) && !loadingImageIds.has(imageId)) {
                const image = images.find(img => img.id === imageId);
                if (image && (!image.image_url || image.image_url === 'base64') && !image.b64_json) {
                  loadImageData(imageId);
                }
              }
            }
          });
        },
        { rootMargin: '100px', threshold: 0.1 }
      );

      imageRefs.current.forEach((el) => {
        if (el) observer.observe(el);
      });
      
      return () => observer.disconnect();
    }, 100);
    
    return () => clearTimeout(timer);
  }, [open, images.length, loadedImages, loadingImageIds, loadImageData]);

  useEffect(() => {
    if (open) {
      loadImages();
      setLoadedImages(new Map());
    }
  }, [open, jobId]);

  const loadImages = async (retryCount = 0) => {
    setLoading(true);
    setLoadError(null);
    try {
      // DON'T fetch b64_json in initial query - lazy load instead
      const { data, error } = await supabase
        .from('generated_images')
        .select('id, image_url, storage_path, prompt, model, created_at')
        .eq('automated_job_id', jobId)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setImages(data || []);
    } catch (error) {
      console.error('Error loading images:', error);
      if (retryCount < 2) {
        setTimeout(() => loadImages(retryCount + 1), 1000);
      } else {
        setLoadError('Failed to load images. Click retry to try again.');
        toast.error('Failed to load images');
      }
    } finally {
      setLoading(false);
    }
  };

  // Realtime updates
  useEffect(() => {
    if (!open) return;
    const channel = supabase
      .channel(`gallery-${jobId}-${Date.now()}`)
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'generated_images',
          filter: `automated_job_id=eq.${jobId}`
        },
        () => {
          loadImages();
        }
      )
      .subscribe();

    const interval = setInterval(() => {
      loadImages();
    }, 20000);

    return () => {
      supabase.removeChannel(channel);
      clearInterval(interval);
    };
  }, [open, jobId]);

  const downloadImage = async (image: GeneratedImage) => {
    try {
      let imageUrl = getDisplayUrl(image);
      
      // ALWAYS fetch from database if image_url is "base64" or we have placeholder
      // This ensures we get the actual b64_json data, not a corrupted fetch
      if (imageUrl === PLACEHOLDER_SVG || image.image_url === 'base64' || !imageUrl.startsWith('data:image/')) {
        toast.loading('Loading image data...', { id: `download-${image.id}` });
        const loadedUrl = await loadImageData(image.id);
        if (!loadedUrl) {
          toast.error('No image data available', { id: `download-${image.id}` });
          return;
        }
        imageUrl = loadedUrl;
        toast.dismiss(`download-${image.id}`);
      }
      
      let mimeType = 'image/jpeg';
      let blob: Blob;
      
      // Now imageUrl should always be a proper data URL
      if (imageUrl.startsWith('data:image/')) {
        mimeType = imageUrl.match(/data:([^;]+)/)?.[1] || 'image/jpeg';
        const base64Data = imageUrl.split(',')[1];
        const binaryString = atob(base64Data);
        const bytes = new Uint8Array(binaryString.length);
        for (let i = 0; i < binaryString.length; i++) {
          bytes[i] = binaryString.charCodeAt(i);
        }
        blob = new Blob([bytes], { type: mimeType });
      } else {
        // Fallback for valid external URLs only
        const response = await fetch(imageUrl);
        blob = await response.blob();
        mimeType = blob.type || 'image/jpeg';
      }
      
      const ext = getExtensionFromMime(mimeType);
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      // Remove number prefix and special characters from job name for filename
      const cleanJobName = jobName.replace(/^\d+[.\s-]+/, '').replace(/[–—]/g, '-').replace(/[^a-z0-9-_\s]/gi, '').trim();
      link.download = `${cleanJobName}_${image.id.slice(0, 8)}.${ext}`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
      toast.success('Image downloaded');
    } catch (error) {
      console.error('Error downloading image:', error);
      toast.error('Failed to download image');
    }
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto bg-background/40 backdrop-blur-md border-border/50">
          <DialogHeader>
            <div className="flex items-center justify-between">
              <DialogTitle>{jobName.replace(/^\d+[.\s-]+/, '')} - Generated Images ({images.length})</DialogTitle>
              <Button size="sm" variant="outline" onClick={() => loadImages()}>
                <RefreshCw className="h-4 w-4 mr-1" />
                Refresh
              </Button>
            </div>
            <DialogDescription className="sr-only">
              Browse and download images generated by this automation job
            </DialogDescription>
          </DialogHeader>

          {loading ? (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-8 w-8 animate-spin text-primary" />
            </div>
          ) : loadError ? (
            <div className="text-center py-12">
              <AlertCircle className="h-8 w-8 mx-auto mb-2 text-yellow-500" />
              <p className="text-muted-foreground mb-3">{loadError}</p>
              <Button size="sm" variant="outline" onClick={() => loadImages()}>
                <RefreshCw className="h-4 w-4 mr-1" />
                Retry Loading
              </Button>
            </div>
          ) : images.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              No images generated yet
            </div>
          ) : (
            <div className="grid grid-cols-2 md:grid-cols-3 gap-4">
              {images.map((image) => (
                <div
                  key={image.id}
                  ref={(el) => {
                    if (el) imageRefs.current.set(image.id, el);
                  }}
                  data-image-id={image.id}
                  className="group relative aspect-square rounded-lg overflow-hidden border border-border/50 bg-background/40 backdrop-blur-sm cursor-pointer hover:ring-2 hover:ring-primary transition-all"
                  onClick={() => setSelectedImage(image)}
                >
                  {loadingImageIds.has(image.id) && (
                    <div className="absolute inset-0 flex items-center justify-center bg-black/50 z-10">
                      <Loader2 className="w-6 h-6 animate-spin text-white" />
                    </div>
                  )}
                  <img
                    src={getDisplayUrl(image)}
                    alt={image.prompt}
                    loading="lazy"
                    decoding="async"
                    onError={(e) => {
                      e.currentTarget.src = '/placeholder.svg';
                    }}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <Button
                      size="sm"
                      variant="secondary"
                      onClick={(e) => {
                        e.stopPropagation();
                        downloadImage(image);
                      }}
                    >
                      <Download className="h-4 w-4 mr-1" />
                      Download
                    </Button>
                  </div>
                  <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-2">
                    <p className="text-xs text-white line-clamp-2">{image.prompt}</p>
                  </div>
                </div>
              ))}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Full Size Image Dialog */}
      <Dialog open={!!selectedImage} onOpenChange={() => setSelectedImage(null)}>
        <DialogContent className="max-w-5xl p-0 bg-background/40 backdrop-blur-md border-border/50">
          <DialogHeader className="sr-only">
            <DialogTitle>Full Size Image</DialogTitle>
            <DialogDescription>View and download full size image</DialogDescription>
          </DialogHeader>
          {selectedImage && (
            <div className="relative">
              <Button
                size="icon"
                variant="ghost"
                className="absolute top-2 right-2 z-10 bg-background/80 backdrop-blur-sm"
                onClick={() => setSelectedImage(null)}
              >
                <X className="h-4 w-4" />
              </Button>
              <img
                src={getDisplayUrl(selectedImage)}
                alt={selectedImage.prompt}
                className="w-full h-auto max-h-[85vh] object-contain"
                onError={(e) => {
                  e.currentTarget.src = '/placeholder.svg';
                }}
              />
              <div className="p-4 bg-background/40 backdrop-blur-sm border-t border-border/50">
                <p className="text-sm font-medium mb-2">{selectedImage.prompt}</p>
                <div className="flex justify-between items-center text-xs text-muted-foreground">
                  <span>Model: {selectedImage.model}</span>
                  <Button
                    size="sm"
                    onClick={() => downloadImage(selectedImage)}
                  >
                    <Download className="h-4 w-4 mr-1" />
                    Download
                  </Button>
                </div>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
